import { useMutation } from '@tanstack/react-query';
import {
    Feedback,
    FeedbackComment,
    FeedbackEmojiReaction,
    FeedbackRequest,
    FeedbackRequestWithDetails,
} from '../../../../common/types/databaseTypes';
import {
    CreateFeedbackParams,
    CreateFeedbackRequestParams,
    feedbackService,
    UpdateFeedbackParams,
    UpdateFeedbackRequestParams,
} from './feedbackService';
import { queryClient } from '@/queryClient';
import { useAuthStore } from '@/stores/authStore';
import { useCurrentWorkspace } from '@/features/workspace';
import {
    useFeedbackCommentNotification,
    useFeedbackReceiveNotification,
    useFeedbackRequestNotification,
    useFeedbackRequestRejectedNotification,
} from '@/features/notification/services/factoryNotificationServices';
import { supabase } from '@tickitacka/shared';
import { useSafeQuery } from '@/common/hooks/useSafeQuery';
import { useEffect, useMemo } from 'react';
import { useDeleteNotificationWithRelatedData } from '@/features/notification/services/useNotificationQueries';
import { NotificationType } from '@/features/notification/constants/notification';
import useFeedbackModalStore from '../hooks/useFeedbackModalStore';
import { EMOJI_REACTION_KEYS } from '@/features/emoji_reaction/hooks/useEmojiReactionQueries';

// 피드백 쿼리 키 상수
export const FEEDBACK_KEYS = {
    all: ['feedbacks'] as const,
    receivedByUser: (userId: string, workspaceId: string) =>
        [...FEEDBACK_KEYS.all, 'received', userId, workspaceId] as const,
    sentByUser: (userId: string, workspaceId: string) =>
        [...FEEDBACK_KEYS.all, 'sent', userId, workspaceId] as const,
    comments: (feedbackId: string) =>
        [...FEEDBACK_KEYS.all, 'comments', feedbackId] as const,

    // 피드백 요청 관련 키
    requestAll: ['feedbackRequest'] as const,
    receivedRequests: (userId: string, workspaceId: string) =>
        [...FEEDBACK_KEYS.requestAll, 'received', userId, workspaceId] as const,
    sentRequests: (userId: string, workspaceId: string) =>
        [...FEEDBACK_KEYS.requestAll, 'sent', userId, workspaceId] as const,
};

/**
 * 특정 사용자가 받은 피드백 목록을 조회하는 훅
 * @param userId 사용자 ID
 * @param workspaceId 워크스페이스 ID
 */
export const useReceivedFeedbacks = (userId: string, workspaceId: string) => {
    const queryKey = useMemo(
        () => [...FEEDBACK_KEYS.receivedByUser(userId, workspaceId)],
        [userId, workspaceId]
    );

    const query = useSafeQuery({
        queryKey,
        queryFn: () =>
            feedbackService.getFeedbacksByUserId(
                userId,
                workspaceId,
                'receiver'
            ),
        enabled: !!userId && !!workspaceId,
    });

    return query;
};

/**
 * 특정 사용자가 작성한 피드백 목록을 조회하는 훅 - 이모지 리액션 실시간 구독
 * @param userId 사용자 ID
 * @param workspaceId 워크스페이스 ID
 */
export const useSentFeedbacks = (userId: string, workspaceId: string) => {
    const queryKey = useMemo(
        () => [...FEEDBACK_KEYS.sentByUser(userId, workspaceId)],
        [userId, workspaceId]
    );

    const query = useSafeQuery({
        queryKey,
        queryFn: () =>
            feedbackService.getFeedbacksByUserId(userId, workspaceId, 'sender'),
        enabled: !!userId && !!workspaceId,
    });

    useEffect(() => {
        if (!userId || !workspaceId) return;

        // 디바운싱을 위한 타이머 ref
        let debounceTimer: NodeJS.Timeout | null = null;

        // 피드백 이모지 리액션 실시간 구독 추가
        const emojiChannel = supabase
            .channel(`feedback-emoji-reactions-sent-${userId}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'feedback_emoji_reaction',
                    // filter: `feedback_id=in.(${query.data?.map(f => f.id).join(',') || ''})`,
                },
                payload => {
                    const feedbackId = (payload.new as FeedbackEmojiReaction)
                        ?.feedback_id;

                    // 이전 타이머가 있으면 취소
                    if (debounceTimer) {
                        clearTimeout(debounceTimer);
                    }

                    // 짧은 디바운싱 (100ms) 적용
                    debounceTimer = setTimeout(() => {
                        // 피드백 데이터 무효화 (이모지 포함해서 다시 조회)
                        void queryClient.invalidateQueries({ queryKey });

                        if (feedbackId) {
                            // INSERT/UPDATE 시에는 특정 피드백만 무효화
                            void queryClient.invalidateQueries({
                                queryKey:
                                    EMOJI_REACTION_KEYS.feedback(feedbackId),
                            });
                        } else {
                            // DELETE 시에는 모든 이모지 리액션 캐시 무효화 (삭제 이벤트 payload에서 feedback_id 가져오기 불가)
                            void queryClient.invalidateQueries({
                                queryKey: EMOJI_REACTION_KEYS.all,
                            });
                        }
                    }, 100);
                }
            )
            .subscribe();

        return () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
            void supabase.removeChannel(emojiChannel);
        };
    }, [query.data, queryKey, userId, workspaceId]);

    return query;
};

/**
 * 받은 피드백 요청 조회 훅
 * @param userId 사용자 ID
 * @param workspaceId 워크스페이스 ID
 */
export const useReceivedFeedbackRequests = (
    userId: string,
    workspaceId: string
) => {
    const { setRequestUpdated } = useFeedbackModalStore();
    const queryKey = useMemo(
        () => [...FEEDBACK_KEYS.receivedRequests(userId, workspaceId)],
        [userId, workspaceId]
    );

    // 실시간 구독 설정
    useEffect(() => {
        if (!userId || !workspaceId) return;

        const channel = supabase
            .channel('feedback-received-requests-changes')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'feedback_request',
                    filter: `receiver_id=eq.${userId}`,
                },
                payload => {
                    const newData = payload.new as FeedbackRequest;

                    // 삭제 처리 시 캐시 무효화
                    if (newData?.enable === false) {
                        void queryClient.invalidateQueries({ queryKey });
                    } else if (payload.eventType === 'UPDATE') {
                        // 삭제가 아닌 수정일 경우, 새로고침 버튼 표시 상태 업데이트
                        setRequestUpdated(true);
                    }
                }
            )
            .subscribe();

        // 컴포넌트 언마운트 시 구독 정리
        return () => {
            void supabase.removeChannel(channel);
        };
    }, [userId, workspaceId, queryKey, setRequestUpdated]);

    return useSafeQuery<FeedbackRequestWithDetails[]>({
        queryKey,
        queryFn: () =>
            feedbackService.getReceivedFeedbackRequests(userId, workspaceId),
        enabled: !!userId && !!workspaceId,
    });
};

/**
 * 보낸 피드백 요청 조회 훅
 * @param userId 사용자 ID
 * @param workspaceId 워크스페이스 ID
 */
export const useSentFeedbackRequests = (
    userId: string,
    workspaceId: string
) => {
    return useSafeQuery<FeedbackRequestWithDetails[]>({
        queryKey: [...FEEDBACK_KEYS.sentRequests(userId, workspaceId)],
        queryFn: () =>
            feedbackService.getSentFeedbackRequests(userId, workspaceId),
        // select: data =>
        //     withSafeUsersArray(data, t, { userFields: ['sender', 'receiver'] }),
        enabled: !!userId && !!workspaceId,
    });
};

/**
 * 피드백의 코멘트 목록을 조회하는 훅
 * @param feedbackId 피드백 ID
 */
export const useFeedbackComments = (feedbackId: string) => {
    return useSafeQuery({
        queryKey: FEEDBACK_KEYS.comments(feedbackId),
        queryFn: () => feedbackService.getFeedbackComments(feedbackId),
        enabled: !!feedbackId,
    });
};

/**
 * 피드백 댓글 실시간 구독 훅
 * @param receivedFeedbackIds 댓글을 구독할 받은 피드백 ID 배열
 * @param sentFeedbackIds 댓글을 구독할 보낸 피드백 ID 배열
 * @param workspaceId 워크스페이스 ID
 */
export const useFeedbackCommentsSubscription = (
    receivedFeedbackIds: string[],
    sentFeedbackIds: string[],
    workspaceId: string
) => {
    useEffect(() => {
        const { user } = useAuthStore.getState();
        const feedbackIds = [...receivedFeedbackIds, ...sentFeedbackIds];
        if (!feedbackIds.length || !workspaceId || !user) return;

        // 피드백 ID 목록에 대한 댓글 구독
        const channel = supabase
            .channel(`feedback-comments-changes`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'feedback_comment',
                    filter: `feedback_id=in.(${feedbackIds.join(',')})`,
                },
                payload => {
                    // 변경된 댓글의 피드백 ID 확인
                    const feedbackId =
                        (payload.new as FeedbackComment)?.feedback_id ||
                        (payload.old as FeedbackComment)?.feedback_id;
                    if (feedbackId) {
                        // 피드백 목록 쿼리 무효화
                        if (receivedFeedbackIds.includes(feedbackId)) {
                            void queryClient.invalidateQueries({
                                queryKey: FEEDBACK_KEYS.receivedByUser(
                                    user.id,
                                    workspaceId
                                ),
                            });
                        } else if (sentFeedbackIds.includes(feedbackId)) {
                            void queryClient.invalidateQueries({
                                queryKey: FEEDBACK_KEYS.sentByUser(
                                    user.id,
                                    workspaceId
                                ),
                            });
                        }

                        // 해당 피드백의 댓글 쿼리 무효화
                        void queryClient.invalidateQueries({
                            queryKey: FEEDBACK_KEYS.comments(feedbackId),
                        });
                    }
                }
            )
            .subscribe();

        // 클린업: 구독 해제
        return () => {
            void supabase.removeChannel(channel);
        };
    }, [receivedFeedbackIds, sentFeedbackIds, workspaceId]);
};

/**
 * 피드백을 생성하는 뮤테이션 훅
 */
export const useCreateFeedback = () => {
    const { createFeedbackReceiveNotification } =
        useFeedbackReceiveNotification();

    return useMutation({
        mutationFn: (feedbackParams: CreateFeedbackParams) =>
            feedbackService.createFeedback(feedbackParams),
        onSuccess: async (createdFeedback, feedbackParams) => {
            // 발신자의 보낸 피드백 목록 갱신
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.sentByUser(
                    feedbackParams.feedback.register_id,
                    feedbackParams.feedback.workspace_id
                ),
            });

            // 발신자의 받은 피드백 요청 목록 갱신
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.receivedRequests(
                    feedbackParams.feedback.register_id,
                    feedbackParams.feedback.workspace_id
                ),
            });

            // 피드백 수신자에게 알림 발송
            try {
                const { user } = useAuthStore.getState();
                if (!user) {
                    throw new Error('로그인이 필요합니다.');
                }

                await createFeedbackReceiveNotification({
                    userId: createdFeedback.receiver_id,
                    params: {
                        is_updated: false,
                        feedback_id: createdFeedback.id,
                        feedback_content: createdFeedback.content,
                        writer_id: user.id,
                        writer_name: user.name,
                        writer_image_url: user.profile_image_url,
                        workspace_id: createdFeedback.workspace_id,
                        workspace_name: feedbackParams.notiParams.workspaceName,
                        workspace_image_url:
                            feedbackParams.notiParams.workspaceImageUrl,
                    },
                });
            } catch (e) {
                console.error('피드백 수신자에게 알림 발송 실패:', e);
            }
        },
    });
};

/**
 * 피드백을 수정하는 뮤테이션 훅
 */
export const useUpdateFeedback = () => {
    const { createFeedbackReceiveNotification } =
        useFeedbackReceiveNotification();
    const deleteNotification = useDeleteNotificationWithRelatedData();

    return useMutation({
        mutationFn: (params: UpdateFeedbackParams) =>
            feedbackService.updateFeedback(params),
        onSuccess: async (updatedFeedback, feedbackParams) => {
            // 작성자의 보낸 피드백 목록 갱신
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.sentByUser(
                    updatedFeedback.register_id,
                    updatedFeedback.workspace_id
                ),
            });

            try {
                const { user } = useAuthStore.getState();
                if (!user) {
                    throw new Error('로그인이 필요합니다.');
                }

                // 피드백 수신자에게 알림 발송
                try {
                    await createFeedbackReceiveNotification({
                        userId: updatedFeedback.receiver_id,
                        params: {
                            is_updated: true,
                            feedback_id: updatedFeedback.id,
                            feedback_content: updatedFeedback.content,
                            writer_id: user.id,
                            writer_name: user.name,
                            writer_image_url: user.profile_image_url,
                            workspace_id: updatedFeedback.workspace_id,
                            workspace_name:
                                feedbackParams.notiParams.workspaceName,
                            workspace_image_url:
                                feedbackParams.notiParams.workspaceImageUrl,
                        },
                    });
                } catch (e) {
                    console.error('피드백 수신자에게 알림 발송 실패:', e);
                }

                // 기존 알림 삭제
                try {
                    await deleteNotification.mutateAsync({
                        userId: updatedFeedback.receiver_id,
                        relatedId: updatedFeedback.id,
                        type: NotificationType.FEEDBACK_RECEIVED,
                    });
                } catch (e) {
                    console.error('기존 알림 삭제 실패:', e);
                }
            } catch (e) {
                console.error('사용자 인증 정보 가져오기 실패:', e);
            }
        },
    });
};

/**
 * 피드백을 읽음 상태로 변경하는 뮤테이션 훅
 */
export const useMarkFeedbackAsRead = () => {
    return useMutation({
        mutationFn: (feedbackId: string) =>
            feedbackService.markFeedbackAsRead(feedbackId),
        onSuccess: updatedFeedback => {
            // 수신자의 받은 피드백 목록 갱신
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.receivedByUser(
                    updatedFeedback.receiver_id,
                    updatedFeedback.workspace_id
                ),
            });
        },
    });
};

/**
 * 피드백을 숨김 처리하는 뮤테이션 훅
 */
export const useHideFeedbackForUser = () => {
    return useMutation({
        mutationFn: (feedback: Feedback) =>
            feedbackService.hideFeedbackForUser(feedback.id),
        onSuccess: (feedback: Feedback) => {
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.receivedByUser(
                    feedback.receiver_id,
                    feedback.workspace_id
                ),
            });
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.sentByUser(
                    feedback.register_id,
                    feedback.workspace_id
                ),
            });
        },
    });
};

/**
 * 피드백 북마크 상태를 변경하는 뮤테이션 훅
 */
export const useToggleFeedbackBookmark = () => {
    return useMutation({
        mutationFn: ({
            feedback,
            isBookmarked = true,
        }: {
            feedback: Feedback;
            isBookmarked?: boolean;
        }) => feedbackService.toggleFeedbackBookmark(feedback.id, isBookmarked),
        onSuccess: (feedback: Feedback) => {
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.receivedByUser(
                    feedback.receiver_id,
                    feedback.workspace_id
                ),
            });
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.sentByUser(
                    feedback.register_id,
                    feedback.workspace_id
                ),
            });
        },
    });
};

/**
 * 피드백에 코멘트를 추가하는 뮤테이션 훅
 */
export const useAddFeedbackComment = () => {
    const { createFeedbackCommentNotification } =
        useFeedbackCommentNotification();

    return useMutation({
        mutationFn: ({
            feedbackId,
            content,
            parentId,
            notiParams,
        }: {
            feedbackId: string;
            content: string;
            parentId?: string;
            notiParams: {
                workspaceId: string;
                workspaceName: string;
                workspaceImageUrl: string;
            };
        }) => feedbackService.addFeedbackComment(feedbackId, content, parentId),
        onSuccess: async (createdComment, { feedbackId, notiParams }) => {
            const { user } = useAuthStore.getState();

            // 코멘트 목록 갱신
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.comments(feedbackId),
            });

            // 수신/발신 피드백 리스트 갱신
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.receivedByUser(
                    user?.id,
                    notiParams.workspaceId
                ),
            });

            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.sentByUser(
                    user?.id,
                    notiParams.workspaceId
                ),
            });

            // 피드백 관련자에게 알림 발송
            try {
                const { user } = useAuthStore.getState();
                if (!user) {
                    throw new Error('로그인이 필요합니다.');
                }

                // 1. 피드백 데이터 조회
                const feedback =
                    await feedbackService.getFeedbackById(feedbackId);

                const notiReceiverId =
                    feedback.receiver_id === user.id
                        ? feedback.register_id
                        : feedback.receiver_id;
                const isFeedbackReceiver =
                    feedback.receiver_id === notiReceiverId;

                await createFeedbackCommentNotification({
                    userId: notiReceiverId,
                    params: {
                        is_feedback_receiver: isFeedbackReceiver,
                        comment_id: createdComment.id,
                        comment_text: createdComment.content,
                        is_reply: createdComment.parent_comment_id
                            ? true
                            : false,
                        parent_comment_id: createdComment.parent_comment_id,
                        commenter_name: user.name,
                        commenter_image_url: user.profile_image_url,
                        commenter_id: user.id,
                        feedback_id: feedbackId,
                        feedback_content: feedback.content,
                        workspace_id: notiParams.workspaceId,
                        workspace_name: notiParams.workspaceName,
                        workspace_image_url: notiParams.workspaceImageUrl,
                    },
                });
            } catch (e) {
                console.error('피드백 관련자에게 댓글 알림 발송 실패:', e);
            }
        },
    });
};

/**
 * 피드백 코멘트를 수정하는 뮤테이션 훅
 */
export const useUpdateFeedbackComment = () => {
    const { user } = useAuthStore.getState();
    const { currentWorkspace } = useCurrentWorkspace(user?.id);

    return useMutation({
        mutationFn: ({
            feedbackCommentId,
            content,
        }: {
            feedbackCommentId: string;
            content: string;
        }) => feedbackService.updateFeedbackComment(feedbackCommentId, content),
        onSuccess: updatedComment => {
            // 작성자의 보낸 피드백 목록 갱신
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.sentByUser(
                    user?.id || '',
                    currentWorkspace?.id || ''
                ),
            });

            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.receivedByUser(
                    user?.id || '',
                    currentWorkspace?.id || ''
                ),
            });
        },
    });
};

/**
 * 피드백 코멘트를 삭제하는 뮤테이션 훅
 */
export const useDeleteFeedbackComment = () => {
    const { user } = useAuthStore.getState();
    const { currentWorkspace } = useCurrentWorkspace(user?.id);

    return useMutation({
        mutationFn: (feedbackCommentId: string) =>
            feedbackService.deleteFeedbackComment(feedbackCommentId),
        onSuccess: (_, feedbackCommentId) => {
            // 작성자의 보낸 피드백 목록 갱신
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.sentByUser(
                    user?.id || '',
                    currentWorkspace?.id || ''
                ),
            });

            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.receivedByUser(
                    user?.id || '',
                    currentWorkspace?.id || ''
                ),
            });

            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.comments(feedbackCommentId),
            });
        },
    });
};

/**
 * 피드백 요청을 생성하는 뮤테이션 훅
 */
export const useCreateFeedbackRequest = () => {
    const { createFeedbackRequestNotification } =
        useFeedbackRequestNotification();

    return useMutation({
        mutationFn: (newFeedbackRequest: CreateFeedbackRequestParams) =>
            feedbackService.createFeedbackRequest(newFeedbackRequest),
        onSuccess: async (
            createdFeedbackRequest,
            { feedbackRequest, notiParams }
        ) => {
            // 발신자의 보낸 피드백 요청 목록 갱신
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.sentRequests(
                    feedbackRequest.register_id,
                    feedbackRequest.workspace_id
                ),
            });

            // 피드백 요청 수신자에게 알림 발송
            try {
                const { user } = useAuthStore.getState();
                if (!user) {
                    throw new Error('로그인이 필요합니다.');
                }

                await createFeedbackRequestNotification({
                    userId: createdFeedbackRequest.receiver_id,
                    params: {
                        is_updated: false,
                        request_id: createdFeedbackRequest.id,
                        request_content: createdFeedbackRequest.content,
                        requester_id: user.id,
                        requester_name: user.name,
                        requester_image_url: user.profile_image_url,
                        workspace_id: feedbackRequest.workspace_id,
                        workspace_name: notiParams.workspaceName,
                        workspace_image_url: notiParams.workspaceImageUrl,
                    },
                });
            } catch (e) {
                console.error('피드백 요청 수신자에게 알림 발송 실패:', e);
            }
        },
    });
};

/**
 * 피드백 요청을 수정하는 뮤테이션 훅
 */
export const useUpdateFeedbackRequest = () => {
    const { createFeedbackRequestNotification } =
        useFeedbackRequestNotification();
    const deleteNotification = useDeleteNotificationWithRelatedData();

    return useMutation({
        mutationFn: (params: UpdateFeedbackRequestParams) =>
            feedbackService.updateFeedbackRequest(params),
        onSuccess: async (updatedRequest, { notiParams }) => {
            // 발신자의 보낸 피드백 요청 목록 갱신
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.sentRequests(
                    updatedRequest.register_id,
                    updatedRequest.workspace_id
                ),
            });

            try {
                const { user } = useAuthStore.getState();
                if (!user) {
                    throw new Error('로그인이 필요합니다.');
                }

                // 피드백 요청 수신자에게 알림 발송
                try {
                    await createFeedbackRequestNotification({
                        userId: updatedRequest.receiver_id,
                        params: {
                            is_updated: true,
                            request_id: updatedRequest.id,
                            request_content: updatedRequest.content,
                            requester_id: user.id,
                            requester_name: user.name,
                            requester_image_url: user.profile_image_url,
                            workspace_id: updatedRequest.workspace_id,
                            workspace_name: notiParams.workspaceName,
                            workspace_image_url: notiParams.workspaceImageUrl,
                        },
                    });
                } catch (e) {
                    console.error('피드백 요청 수신자에게 알림 발송 실패:', e);
                }

                // 기존 알림 삭제
                try {
                    await deleteNotification.mutateAsync({
                        userId: updatedRequest.receiver_id,
                        relatedId: updatedRequest.id,
                        type: NotificationType.FEEDBACK_REQUEST,
                    });
                } catch (e) {
                    console.error('기존 알림 삭제 실패:', e);
                }
            } catch (e) {
                console.error('사용자 인증 정보 가져오기 실패:', e);
            }
        },
    });
};

/**
 * 피드백 요청을 삭제하는 뮤테이션 훅 (요청 취소)
 */
export const useDeleteFeedbackRequest = () => {
    return useMutation({
        mutationFn: (feedbackRequest: FeedbackRequest) =>
            feedbackService.deleteFeedbackRequest(feedbackRequest.id),
        onSuccess: (_, feedbackRequest) => {
            // 발신자의 보낸 피드백 요청 목록 갱신
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.sentRequests(
                    feedbackRequest.register_id,
                    feedbackRequest.workspace_id
                ),
            });
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.receivedRequests(
                    feedbackRequest.receiver_id,
                    feedbackRequest.workspace_id
                ),
            });
        },
    });
};

/**
 * 피드백 요청을 거절하는 뮤테이션 훅
 */
export const useRejectFeedbackRequest = () => {
    const { createFeedbackRequestRejectedNotification } =
        useFeedbackRequestRejectedNotification();

    return useMutation({
        mutationFn: ({
            feedbackRequest,
            notiParams,
        }: {
            feedbackRequest: FeedbackRequest;
            notiParams: {
                workspaceName: string;
                workspaceImageUrl: string;
            };
        }) => feedbackService.rejectFeedbackRequest(feedbackRequest.id),
        onSuccess: async (_, { feedbackRequest, notiParams }) => {
            // 수신자의 받은 피드백 요청 목록 갱신
            void queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.receivedRequests(
                    feedbackRequest.receiver_id,
                    feedbackRequest.workspace_id
                ),
            });

            // 피드백 요청자에게 거절 알림 발송
            try {
                const { user } = useAuthStore.getState();
                if (!user) {
                    throw new Error('로그인이 필요합니다.');
                }

                await createFeedbackRequestRejectedNotification({
                    userId: feedbackRequest.register_id,
                    params: {
                        request_id: feedbackRequest.id,
                        rejecter_id: user.id,
                        rejecter_name: user.name,
                        rejecter_image_url: user.profile_image_url,
                        workspace_id: feedbackRequest.workspace_id,
                        workspace_name: notiParams.workspaceName,
                        workspace_image_url: notiParams.workspaceImageUrl,
                    },
                });
            } catch (e) {
                console.error('피드백 요청자에게 거절 알림 발송 실패:', e);
            }
        },
    });
};
